import { useCallback, useRef } from 'react';
import { updateExperienceFieldsBatch } from '@/data/experiencesRepo';
import type { ExperienceType } from '@/types/types_experience';
import type { HistoryInfoType } from '@/types/types_history';
import { useCurrentUserSimple } from './useCurrentUserSimple';
import { applyHistoryToUpdate } from './util_history';

// Autosave for the home page's featured ordering, which is the one experience
// edit that spans several documents: featuring one experience renumbers the
// others around it.
//
// It goes through `applyHistoryToUpdate` like every other edit, and has to. A
// plain `updateDoc` on a historized document does not merely skip the log — the
// `historysteps` Cloud Function still fires, sees `history.currentStepId`
// unchanged, and re-archives the document under that same id, overwriting the
// archived copy of whatever edit came before with one that has a different
// `featuredOrder`. The step in the history panel then shows a state that never
// existed. Appending a step is what keeps each archived copy matched to the
// edit that produced it.

/** What one experience's position should become. `null` removes it from the section. */
export interface FeaturedOrderType {
  experience: ExperienceType;
  featuredOrder: number | null;
}

/** The `stepAction` for a move, in the terms the section is curated in. */
function describeFeaturedChange(from: number | null | undefined, to: number | null): string {
  if (to === null) return 'Removed from Featured Experiences';
  if (typeof from !== 'number') return `Added to Featured Experiences at ${to}`;
  return `Moved to ${to} in Featured Experiences`;
}

/**
 * Returns a `save(orders)` function that writes the changed positions, each with
 * a history step, in one batch.
 *
 * Positions that are already right are dropped rather than rewritten, so a drag
 * that moves one experience past two others logs three steps instead of one per
 * featured experience. That matters: only `PRESERVE_HISTORY_STEPS` steps stay on
 * the document, and a renumber touching the whole list would push real content
 * edits out of the panel every time somebody reordered the home page.
 */
export function useAutosaveFeaturedOrders() {
  const userSimple = useCurrentUserSimple();

  // What this hook last wrote for each experience, by id.
  //
  // The caller's experiences come from the Firestore snapshot, which does not
  // arrive until after the write it describes. Two drags in quick succession
  // would therefore both read the same stored `history` and mint the same step
  // number, and both compare against the same stored `featuredOrder` — so a move
  // away and back again would be judged a no-op and never undo the first write.
  // Holding what was actually written closes both gaps.
  const written = useRef(new Map<string, { featuredOrder: number | null; history: HistoryInfoType }>());

  return useCallback(
    async (orders: FeaturedOrderType[]): Promise<void> => {
      const touched: string[] = [];
      const updates: { id: string; updateObj: Record<string, unknown> }[] = [];

      for (const { experience, featuredOrder } of orders) {
        const last = written.current.get(experience.id);
        // A stored history further along than ours means the snapshot has caught
        // up (or somebody else edited the experience); take it and drop the cache.
        const stale = last !== undefined
          && experience.history !== undefined
          && experience.history.lastStep >= last.history.lastStep;
        if (stale) written.current.delete(experience.id);

        const current = stale || !last
          ? { featuredOrder: experience.featuredOrder, history: experience.history }
          : last;

        if ((current.featuredOrder ?? null) === featuredOrder) continue;

        const { updateObj, newHistory } = applyHistoryToUpdate(
          { featuredOrder },
          describeFeaturedChange(current.featuredOrder, featuredOrder),
          current.history,
          userSimple,
          false,
        );
        written.current.set(experience.id, { featuredOrder, history: newHistory });
        touched.push(experience.id);
        updates.push({ id: experience.id, updateObj });
      }

      try {
        await updateExperienceFieldsBatch(updates);
      } catch (e) {
        // Nothing landed — the batch is atomic — so the cache must not claim the
        // steps it minted, or the next save would number itself past them.
        for (const id of touched) written.current.delete(id);
        throw e;
      }
    },
    [userSimple],
  );
}
